/** Keeps the console-wide tenant scope and the URL in agreement.
 *
 * Mounted once, inside `AppShell` (so it never runs on the public `/auth/*`
 * routes). On every location change:
 *
 * | URL           | store        | action                                     |
 * |---------------|--------------|--------------------------------------------|
 * | `scope=X`     | X            | nothing                                    |
 * | `scope=X` new | Y / empty    | adopt X — an explicit param always wins    |
 * | no `scope`    | X            | stamp `?scope=X` (replace)                 |
 * | no `scope`    | empty        | nothing                                    |
 *
 * X is a list of operator ids (`?scope=12,845`, sorted); the comparisons are on
 * that sorted list, so a hand-edited order or a duplicate is no change.
 *
 * "new" is the load-bearing word, tracked in `lastUrlScope`. Without it,
 * resetting the scope would be undone one tick later: the store goes empty
 * while the URL still says `scope=X`, which looks exactly like an explicit
 * `?scope=X` link and would be re-adopted. Comparing against the URL scope we
 * last reconciled tells the two apart — a value we have already seen means the
 * STORE changed, so the store wins; a value we have not means the URL changed
 * (initial load, Back/Forward, a scope-carrying link), so the URL wins.
 *
 * Every write is `{ replace: true }` — the scope must never add history
 * entries of its own, but it does ride along in the ones the app creates, which
 * is what makes Back/Forward restore the scope that was active at the time.
 */
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTenantBriefs } from '@/components/TenantPicker/useTenantBriefs';

import { SCOPE_PARAM, readScope } from './scopeUrl';
import { formatTenantIds } from './tenantIds';
import { getTenantScopeList, setTenantScopeList, useTenantScopeList } from './tenantScope';

/** A scope adopted from a bare `?scope=123` has no name yet. */
const isPlaceholderName = (s: { id: number; name: string }) => s.name === `#${s.id}`;

export function TenantScopeUrlSync() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeList = useTenantScopeList();
  const scopeKey = formatTenantIds(scopeList.map((s) => s.id));

  // The URL scope we last reconciled. `undefined` until the first run so an
  // initial load is treated as "the URL changed".
  const lastUrlScope = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const urlIds = readScope(searchParams);
    const urlScope = formatTenantIds(urlIds);
    if (urlScope === scopeKey) {
      lastUrlScope.current = urlScope;
      return;
    }

    const urlChanged = lastUrlScope.current === undefined || urlScope !== lastUrlScope.current;
    if (urlChanged && urlScope !== null) {
      // An explicit `?scope=` we have not seen before wins over the tab's scope.
      // Names the tab already knows carry over; the rest resolve below.
      const known = getTenantScopeList();
      setTenantScopeList(urlIds.map((id) => known.find((s) => s.id === id) ?? { id, name: `#${id}` }));
      lastUrlScope.current = urlScope;
      return;
    }

    // The store is authoritative: stamp it in, or strip the param when cleared.
    lastUrlScope.current = scopeKey;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (scopeKey === null) next.delete(SCOPE_PARAM);
        else next.set(SCOPE_PARAM, scopeKey);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, scopeKey, setSearchParams]);

  // Resolve the real names in ONE batched lookup, so a shared `?scope=12,845`
  // link never leaves the chip reading "#12".
  const placeholders = scopeList.filter(isPlaceholderName).map((s) => s.id);
  const briefs = useTenantBriefs(placeholders);

  useEffect(() => {
    if (briefs.size === 0) return;
    // Read the store at WRITE time, not from this render's snapshot. An
    // in-flight lookup can resolve in the same tick as an adopt (Back/Forward
    // across a scope change does exactly this), and a render that still names
    // the old operators would otherwise write their names back over the scope
    // that just changed.
    const current = getTenantScopeList();
    let changed = false;
    const next = current.map((s) => {
      const b = isPlaceholderName(s) ? briefs.get(s.id) : undefined;
      if (!b) return s;
      changed = true;
      return { id: s.id, name: b.name, ...(b.code ? { code: b.code } : {}) };
    });
    if (changed) setTenantScopeList(next);
  }, [briefs]);

  return null;
}
